// Renders the Google Identity Services (GIS) "Continue with Google" button and
// hands the resulting ID-token credential to the caller. Renders nothing when
// REACT_APP_GOOGLE_CLIENT_ID is unset, so the feature ships dark until configured.
// Docs: docs/integrations/google-sign-in.md
import React, { useEffect, useRef } from 'react';

const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
const GSI_SRC = 'https://accounts.google.com/gsi/client';

declare global {
  interface Window {
    google?: any;
  }
}

let gsiScriptPromise: Promise<void> | null = null;

function loadGsiScript(): Promise<void> {
  if (!gsiScriptPromise) {
    gsiScriptPromise = new Promise((resolve, reject) => {
      if (window.google?.accounts?.id) {
        resolve();
        return;
      }
      const script = document.createElement('script');
      script.src = GSI_SRC;
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => {
        gsiScriptPromise = null; // allow a retry on next mount
        reject(new Error('Failed to load Google Sign-In script'));
      };
      document.head.appendChild(script);
    });
  }
  return gsiScriptPromise;
}

interface GoogleSignInButtonProps {
  onCredential: (credential: string) => void;
  /** Optional consent/disclosure line rendered directly under the button (e.g. "By continuing, you agree to ..."). */
  disclosure?: React.ReactNode;
}

const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({ onCredential, disclosure }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const onCredentialRef = useRef(onCredential);
  onCredentialRef.current = onCredential;

  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;

    loadGsiScript()
      .then(() => {
        if (cancelled || !containerRef.current || !window.google?.accounts?.id) return;
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (response: { credential: string }) => onCredentialRef.current(response.credential),
        });
        window.google.accounts.id.renderButton(containerRef.current, {
          type: 'standard',
          theme: 'outline',
          size: 'large',
          text: 'continue_with',
          width: 368,
        });
      })
      .catch((err) => {
        console.warn('Google Sign-In unavailable:', err.message);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  if (!GOOGLE_CLIENT_ID) return null;

  // Rendered above the email/password form: button first, then an "or" divider
  return (
    <>
      <div ref={containerRef} style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }} />
      {disclosure && (
        <p style={{ textAlign: 'center', fontSize: '0.8rem', color: 'var(--text-muted, #888)', margin: '0.5rem 0 0' }}>
          {disclosure}
        </p>
      )}
      <div
        aria-hidden
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '0.75rem',
          margin: '1rem 0',
          color: 'var(--text-muted, #888)',
          fontSize: '0.875rem',
        }}
      >
        <span style={{ flex: 1, borderTop: '1px solid var(--border, #ddd)' }} />
        or
        <span style={{ flex: 1, borderTop: '1px solid var(--border, #ddd)' }} />
      </div>
    </>
  );
};

export default GoogleSignInButton;
